import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Grid,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router-dom';
import { getBankFileRecordsById } from 'service';
import { colorTheme } from 'theme';
import {
  bankFileStatusOptions,
  commonConstantTexts,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { AlertModal, CustomDataGrid, DialogBox, Header } from 'components';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { dateFormat } from 'utils/GeneralUtil';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { setSnackBarSuccess } from 'store/snackBar';
import {
  updateBankRecordFileStatus,
  updateTransactionNarrativeForReconcile,
} from 'service/UploadService';
import { CustomTextInput } from 'components/shared';
import Buttons from 'components/elements/Buttons';
import InfoIcon from '../../../assets/images/InfoIcon.png';
import InfoModalGrid from '../InfoModalGrid';

export interface BankFileRecordType {
  transactionDate: string;
  transactionReference: string;
  transactionNarrative: string;
  creditorName: string;
  amount: string;
  status: string;
}

type GridRowData = Record<string, unknown>;

const useStyles = makeStyles({
  emptyTransactionContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70vh',
    width: '100%',
  },
  emptyTransactionText: {
    fontFamily: 'Inter!important',
    color: '#838383',
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.2,
  },
  uploadDetailsGrid: {
    cursor: 'pointer',
  },
});

interface StatusCellProps {
  row: GridValidRowModel;
  handleSelectStatus: (
    event: React.ChangeEvent<{ value: unknown }>,
    row: GridValidRowModel,
  ) => void;
}

const StatusCell: React.FC<StatusCellProps> = ({ row, handleSelectStatus }) => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<BankFileRecordType | null>(null);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ width: 150, display: 'flex', alignItems: 'center' }}>
        {row.status === commonConstantTexts.reconciled ? (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.status}
          </Typography>
        ) : (
          <FormControl fullWidth>
            <Select
              label="Status"
              value={row.status}
              onChange={(event) => {
                handleSelectStatus(event, row);
              }}
              sx={{
                textAlign: 'left',
                '& .MuiSelect-select': {
                  textAlign: 'left',
                  paddingLeft: '0px',
                },
              }}
            >
              {bankFileStatusOptions.map((option, i) => (
                <MenuItem
                  key={i}
                  value={option.value}
                  disabled={option.value === row.status}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {((row?.reconciledBankRecordFileIds?.length > 1 &&
          row?.statusType === commonConstantTexts.automatic) ||
          row?.statusType === commonConstantTexts.manual) &&
          row?.status === commonConstantTexts.reconciled && (
            <IconButton
              onClick={() => {
                setOpenDialog(true);
                setSelectedRecord(row);
              }}
            >
              <Box
                component="img"
                sx={{
                  height: 20,
                  width: 20,
                }}
                src={InfoIcon}
              />
            </IconButton>
          )}
      </Box>
      <DialogBox
        open={row.status === commonConstantTexts.reconciled && openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        title={`${row.statusType || ''} ${commonConstantTexts.reconcilation}`}
        fullWidth={true}
      >
        <InfoModalGrid selectedRecord={selectedRecord} />
      </DialogBox>
    </div>
  );
};

const UploadDetailsGrid = (): JSX.Element => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  const [bankFileRecordsList, setBankFileRecordsList] = useState<
    BankFileRecordType[] | unknown
  >([]);
  const [loader, setLoader] = useState(false);
  const [reconLoader, setReconLoader] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.uploadDetails);
  const [openReconcileModal, setOpenReconcileModal] = useState(false);
  const [toBeReconcileRecord, setToBeReconcileRecord] = useState('');
  const [openOtherPaymentsModal, setOpenOtherPaymentsModal] = useState(false);
  const [toBeOtherPaymentRecord, setToBeOtherPaymentRecord] = useState('');
  const [transactionNarrative, setTransactionNarrative] = useState('');

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getBankFileRecords = async (id: string) => {
    setLoader(true);
    await getBankFileRecordsById(
      id,
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
    )
      .then((result) => {
        setBankFileRecordsList(result?.rows);
        setPageCount(result?.count);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        console.log(err);
      });
  };

  useEffect(() => {
    if (id) {
      void getBankFileRecords(id);
    }
  }, [id, pageInfo?.page, pageInfo?.pageSize]);

  const handleSelectStatus = (event, row): void => {
    event.stopPropagation();
    if (event.target.value === commonConstantTexts.reconciled) {
      setOpenReconcileModal(true);
      setToBeReconcileRecord({ ...row, status: event.target.value });
    } else {
      setOpenOtherPaymentsModal(true);
      setToBeOtherPaymentRecord({ ...row, status: event.target.value });
    }
  };

  const handleMarkAsStatus = async (): Promise<void> => {
    try {
      if (toBeOtherPaymentRecord) {
        const success = await updateBankRecordFileStatus(
          toBeOtherPaymentRecord.id,
          toBeOtherPaymentRecord.status,
        );
        if (success) {
          dispatch(
            setSnackBarSuccess({
              snackBarMessage:
                strings.displayText.transactionMarkedSuccessfully,
            }),
          );
          setOpenOtherPaymentsModal(false);
          setToBeOtherPaymentRecord('');
        }
        await getBankFileRecords(id);
      }
    } catch (error) {
      console.error(strings.displayText.statusMarkingError, error);
    }
  };

  const handleManualReconcile = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      if (toBeReconcileRecord) {
        setReconLoader(true);
        await updateTransactionNarrativeForReconcile(
          toBeReconcileRecord.id,
          transactionNarrative,
        );
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.transactionMarkedSuccessfully,
          }),
        );
        setReconLoader(false);
        setOpenReconcileModal(false);
        setToBeReconcileRecord('');
        setTransactionNarrative('');
      }
    } catch (error) {
      setReconLoader(false);
      setOpenReconcileModal(false);
      setToBeReconcileRecord('');
      setTransactionNarrative('');
    }
    await getBankFileRecords(id);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'transactionDate',
      flex: 1.4,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.date)}
        </Typography>
      ),
    },
    {
      field: 'transactionReference',
      flex: 1.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionReference}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionReference || '-'}
        </Typography>
      ),
    },
    {
      field: 'transactionNarrative',
      flex: 1.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionNarrative}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.transactionNarrative || '-'}
        </Typography>
      ),
    },
    {
      field: 'debtorName',
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.debtorName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.debtorName}
        </Typography>
      ),
    },
    {
      field: 'creditorName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditorName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.creditorName}
        </Typography>
      ),
    },
    {
      field: 'creditOrDebit',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditOrDebit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundFlowType || ''}
        </Typography>
      ),
    },
    {
      field: 'amount',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(Math.abs(row?.amount)).toFixed(2)) || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      flex: 1.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <StatusCell row={row} handleSelectStatus={handleSelectStatus} />
      ),
    },
  ];

  return (
    <Box>
      {loader ? (
        <Box className={classes.emptyTransactionContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <Box className={classes.uploadDetailsGrid}>
          <Box sx={{ display: 'flex' }}>
            <Header
              title={bankFileRecordsList[0]?.contributionBankFile?.fileName}
            />
          </Box>
          <CustomDataGrid
            columns={columns}
            rows={bankFileRecordsList as GridRowData[]}
            getRowId={(row: GridRowData) => `${String(row?.id)}`}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? commonConstantTexts.evenText
                : commonConstantTexts.oddText
            }
            currentPage="uploadDetails"
            pageCount={pageCount}
          />
          <DialogBox
            open={openReconcileModal}
            onClose={() => {
              setTransactionNarrative('');
              setOpenReconcileModal(false);
            }}
            title={strings.displayText.transactionNarrative}
          >
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleManualReconcile}
            >
              <Grid item xs={6}>
                <CustomTextInput
                  value={transactionNarrative}
                  required={true}
                  label={strings.displayText.transactionNarrative}
                  name={strings.displayText.transactionNarrative}
                  onChange={(e) => {
                    setTransactionNarrative(e.target.value);
                  }}
                />
              </Grid>
              <Buttons
                disabled={!transactionNarrative || reconLoader}
                style={{ py: '0.8rem', mt: '1rem' }}
                type="submit"
                fullWidth={true}
                endIcon={reconLoader ? <CircularProgress size={15} /> : null}
              >
                {strings.displayText.saveText}
              </Buttons>
            </Box>
          </DialogBox>

          <AlertModal
            open={openOtherPaymentsModal}
            onClose={() => {
              setOpenOtherPaymentsModal(!openOtherPaymentsModal);
            }}
            title={strings.displayText.areYouConfirm}
            isButtonForConfirmation={true}
            handleConfirm={handleMarkAsStatus}
          />
        </Box>
      )}
    </Box>
  );
};

export default UploadDetailsGrid;
